import { Box, Button, Flex, Input } from "@mantine/core";
import { IconTransactionEuro, IconZoomReset } from "@tabler/icons-react";
import {
  MRT_ColumnDef,
  MRT_RowSelectionState,
  MantineReactTable,
  useMantineReactTable,
} from "mantine-react-table";
import * as React from "react";
import { useDeferredValue, useEffect, useState } from "react";
import { _localization } from "../../../config/location";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import { styleTableMantine } from "../../../_base/_const/_constVar";
import { useDebouncedState } from "@mantine/hooks";
import { _sideNavData } from "../../../_setup/navdata/_sideNavData";
import {
  getAuthKey,
  getAuthToGroup,
  groupAPIAddAuth,
} from "../../../api/ApiAdmin";
import { modals } from "@mantine/modals";

const GroupAPIAuthAdd = ({ idGroup }: { idGroup: string }) => {
  //#region  table
  const [data, setData] = useState<any[]>([]);
  const [dataFirst, setDataFirst] = useState<any[]>([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [loadingButton, setLoadingButton] = useState<boolean>(false);
  //table state

  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [selectIds, setSelectIds] = useState<string[]>([]);
  const rowSelectionDeferred =
    useDeferredValue<MRT_RowSelectionState>(rowSelection);
  const [keySearch, setKeySearch] = useDebouncedState<string>("", 300);

  const getDataAuthKey = async () => {
    setIsLoading(true);
    setIsRefetching(true);
    try {
      const res = await getAuthKey();

      if (res && res.success) {
        const result = res.data;

        setData(result);
        setDataFirst(result);
      }
    } catch (error) {
      console.log(error);
      setIsLoading(false);
      setIsRefetching(false);
    } finally {
      setIsLoading(false);
      setIsRefetching(false);
    }
  };

  const getAuthByGroup = async () => {
    setIsLoading(true);
    setIsRefetching(true);
    try {
      const res = await getAuthToGroup(idGroup);

      if (res && res.success) {
        const result = res.data?.map((item: any) => item.keyAuth);
        const dataRowSelection = res.data?.reduce(
          (acc: Record<string, boolean>, item: any) => {
            acc[item.keyAuth] = true;
            return acc;
          },
          {}
        );
        setSelectIds(result);
        setRowSelection(dataRowSelection);
      }
    } catch (error) {
      console.log(error);
      setIsLoading(false);
      setIsRefetching(false);
    } finally {
      setIsLoading(false);
      setIsRefetching(false);
    }
  };

  const handleAddAuthToGroup = async () => {
    setLoadingButton(true);
    try {
      const dataSubmit = {
        userId: idGroup,
        groupApiIds: selectIds,
      };

      const res = await groupAPIAddAuth(dataSubmit);

      if (res && res.success) {
        NotificationExtension.Success("Phân quyền thành công !");
        setLoadingButton(false);
        modals.closeAll();
      } else {
        NotificationExtension.Fails(res.message);
      }
    } catch (error) {
      console.log(error);
      setLoadingButton(false);
    } finally {
      setLoadingButton(false);
    }
  };

  useEffect(() => {
    getAuthByGroup();
  }, [idGroup]);

  useEffect(() => {
    const valuesList = Object.keys(rowSelectionDeferred);
    setSelectIds(valuesList);
    if (valuesList.length < 1) setSelectIds([]);
    else {
      const valuesList = Object.keys(rowSelectionDeferred);
      setSelectIds(valuesList);
    }
  }, [rowSelectionDeferred]);

  useEffect(() => {
    getDataAuthKey();
  }, []);

  useEffect(() => {
    if (keySearch.length > 0) {
      const filteredData = dataFirst.filter(
        (item) =>
          item.text.toLowerCase().includes(keySearch.toLowerCase()) ||
          item.value.toLowerCase().includes(keySearch.toLowerCase())
      );
      setData(filteredData);
    } else {
      setData(dataFirst);
    }
  }, [keySearch]);

  const columns = React.useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "STT",
        header: "STT",
        Cell: ({ row }) => <>{row.index + 1}</>,
        size: 50,
        enableColumnActions: false,
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "text",
        header: "Tên chức năng",
      },
      {
        accessorKey: "value",
        header: "Đương dẫn",
      },
    ],
    []
  );

  const table = useMantineReactTable({
    columns,
    data,
    positionToolbarAlertBanner: "bottom",
    enablePagination: false,
    enableRowVirtualization: true,
    //add custom action buttons to top-left of top toolbar
    renderTopToolbarCustomActions: ({ table }) => (
      <>
        <Flex
          gap="md"
          justify="flex-start"
          align="center"
          direction="row"
          wrap="wrap"
        >
          <Input
            placeholder="Nhập từ khóa ..."
            defaultValue={keySearch}
            onChange={(e) => setKeySearch(e.currentTarget.value)}
          />
        </Flex>
      </>
    ),
    renderToolbarInternalActions: ({ table }) => (
      <>
        <Flex
          gap="md"
          justify={{ sm: "flex-start", lg: "flex-end" }}
          align="center"
          direction="row"
          wrap="wrap"
        >
          <Button
            leftSection={<IconTransactionEuro size={14} />}
            color=""
            variant="outline"
            loading={loadingButton}
            onClick={() => {
              handleAddAuthToGroup();
            }}
          >
            Phân quyền
          </Button>
          <Button
            leftSection={<IconZoomReset size={14} />}
            color="violet"
            variant="outline"
            onClick={() => {
              setIsLoading(true);
              setTimeout(() => {
                setIsLoading(false);
              }, 500);
            }}
          >
            Load data
          </Button>
        </Flex>
      </>
    ),

    mantineTopToolbarProps: {
      style: {
        borderBottom: "3px solid rgba(128, 128, 128, 0.5)",
        marginBottom: 5,
      },
    },
    localization: _localization,
    enableRowSelection: true,
    getRowId: (row) => row.value,
    initialState: {
      showColumnFilters: false,
      columnPinning: {
        left: ["mrt-row-select"],
      },
      columnVisibility: { id: false },
      density: "xs",
      pagination: {
        pageIndex: 0,
        pageSize: 1000,
      },
    },
    mantineTableContainerProps: {
      style: { maxHeight: "60vh", minHeight: "50vh" },
    },
    enableStickyHeader: true,
    onRowSelectionChange: setRowSelection,
    rowCount: data.length,
    mantineTableBodyCellProps: ({ row }) => ({
      style: styleTableMantine,
    }),
    state: {
      isLoading,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      showSkeletons: isLoading,
      rowSelection,
    },
    mantineToolbarAlertBannerProps: isError
      ? { color: "red", children: "Lỗi tải dữ liệu !" }
      : undefined,
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      rowsPerPageOptions: ["1000"],
    },
    paginationDisplayMode: "pages",
    enableColumnPinning: true,
    mantineTableProps: {
      striped: false,
    },
    rowVirtualizerOptions: {
      overscan: 10,
    },
  });
  return (
    <>
      <Box mt={10} w={"82.5vw"} maw={1250}>
        <MantineReactTable table={table} />
      </Box>
    </>
  );
};
export default GroupAPIAuthAdd;
